import React, { useEffect, useState } from 'react';
import { PageHeader, Card, Button, Modal, FormField, Table, Badge, Alert, Skeleton, SearchInput } from '../components/ui';
import { sprintsAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Plus, CheckCircle, X } from 'lucide-react';

const formatDate = (value) => value ? new Date(value).toLocaleDateString() : '-';

export default function Sprints() {
  const { canCreate } = useAuth();
  const [sprints, setSprints] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ sprint_name: '', capacity: '', start_date: '', end_date: '', goals: '', assigned_user_ids: [] });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [sprintsRes, usersRes] = await Promise.all([
        sprintsAPI.list(),
        canCreate ? usersAPI.list() : Promise.resolve({ data: { data: [] } }),
      ]);
      setSprints(sprintsRes.data.data || []);
      setUsers(usersRes.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [canCreate]);

  const toggleUser = (userId) => {
    setForm((current) => {
      const assigned = new Set(current.assigned_user_ids);
      if (assigned.has(userId)) assigned.delete(userId);
      else assigned.add(userId);
      return { ...current, assigned_user_ids: Array.from(assigned) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        capacity: form.capacity ? Number(form.capacity) : null,
      };
      await sprintsAPI.create(payload);
      setModalOpen(false);
      setForm({ sprint_name: '', capacity: '', start_date: '', end_date: '', goals: '', assigned_user_ids: [] });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create sprint.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'sprint_name', label: 'Sprint' },
    { key: 'capacity', label: 'Capacity' },
    { key: 'start_date', label: 'Start' },
    { key: 'end_date', label: 'End' },
    { key: 'goals', label: 'Goals', render: row => row.goals || '-' },
    { key: 'members', label: 'Members', render: row => (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {row.members?.map((member) => (
          <Badge key={member.sprint_member_id} value={member.user?.name || 'Unknown'} />
        ))}
      </div>
    )},
  ];

  const filteredSprints = sprints.filter(s => {
    if (!search) return true;
    return s.sprint_name?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="animate-in">
      <PageHeader
        title="Plan Sprint"
        subtitle="Create a sprint and assign team members. Assigned users receive a notification."
        eyebrow="Agile"
        action={
          <div style={{ display: 'flex', gap: 12 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search sprints..." />
            {canCreate && (
              <Button onClick={() => setModalOpen(true)}><Plus size={14} /> Plan Sprint</Button>
            )}
          </div>
        }
      />

      <Card>
        {loading ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </div>
        ) : sprints.length === 0 ? (
          <div style={{ padding: 32, color: 'var(--text-muted)' }}>No sprint plans available yet.</div>
        ) : (
          <Table columns={columns} data={filteredSprints} />
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Plan Sprint">
        <Alert type="error" message={error} />
        <form onSubmit={handleSubmit}>
          <FormField label="Sprint name"><input required value={form.sprint_name} onChange={e => setForm(f => ({ ...f, sprint_name: e.target.value }))} /></FormField>
          <FormField label="Capacity (pts)"><input type="number" min="0" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} /></FormField>
          <FormField label="Start date"><input type="date" required value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></FormField>
          <FormField label="End date"><input type="date" required value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} /></FormField>
          <FormField label="Sprint goals"><textarea rows="3" value={form.goals} onChange={e => setForm(f => ({ ...f, goals: e.target.value }))} /></FormField>

          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Assign team members</span>
              {form.assigned_user_ids.length > 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{form.assigned_user_ids.length} selected</span>
              )}
            </div>
            {users.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No team members available.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                {users.map((user) => {
                  const selected = form.assigned_user_ids.includes(user.user_id);
                  return (
                    <button
                      key={user.user_id}
                      type="button"
                      onClick={() => toggleUser(user.user_id)}
                      style={{
                        border: selected ? '1px solid rgba(56,189,248,1)' : '1px solid var(--border)',
                        borderRadius: 14,
                        background: selected ? 'rgba(56,189,248,0.15)' : 'var(--bg-card)',
                        color: 'var(--text)',
                        padding: '14px 12px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        minHeight: 72,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <div style={{ width: 34, height: 34, borderRadius: '9999px', background: selected ? 'rgba(56,189,248,1)' : 'var(--border)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700 }}>
                        {user.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{user.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{user.role}</div>
                      </div>
                      {selected && <CheckCircle size={18} style={{ color: 'rgba(56,189,248,1)' }} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Launch Sprint'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
